import React, { useState, useEffect, useMemo } from "react";
import {
  ShieldCheck,
  Lock,
  Unlock,
  Mail,
  ExternalLink,
  Trash2,
  Download,
  Search,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowLeft,
  MessageSquare,
  Building2,
  Send,
  User,
  Copy,
  Check,
  Eye,
  Sparkles,
  PhoneCall,
  Calendar,
  Globe,
  X,
  Bell,
  Settings,
  Loader2,
  Inbox,
  HelpCircle
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_CONFIG = {
  New: {
    label: "New Inquiry",
    bg: "bg-emerald-500/15",
    text: "text-emerald-400",
    border: "border-emerald-500/30",
    dot: "bg-emerald-400"
  },
  "In Review": {
    label: "In Review",
    bg: "bg-amber-500/15",
    text: "text-amber-400",
    border: "border-amber-500/30",
    dot: "bg-amber-400"
  },
  Contacted: {
    label: "Contacted",
    bg: "bg-cyan-500/15",
    text: "text-cyan-400",
    border: "border-cyan-500/30",
    dot: "bg-cyan-400"
  },
  Completed: {
    label: "Completed",
    bg: "bg-purple-500/15",
    text: "text-purple-400",
    border: "border-purple-500/30",
    dot: "bg-purple-400"
  }
};

const RESPONSE_TEMPLATES = {
  discovery: {
    label: "Strategy & Discovery Call Invitation",
    getSubject: (lead) => `Re: SEO Strategy & Organic Growth - Kanna Narendra`,
    getMessage: (lead) => {
      const firstName = lead.name?.split(" ")[0] || "there";
      const company = lead.company || "your website";
      const needs = lead.needs || "SEO Strategy";
      return `Hi ${firstName},

Thank you for reaching out regarding ${company}!

I have reviewed your inquiry regarding "${needs}" and your note:
"${lead.message || "your organic traffic goals"}"

I would love to set up a brief 20-minute introductory strategy session to walk through actionable high-impact search opportunities for your domain, covering technical architecture, AEO/GEO citations, and high-intent keyword acquisition.

Could you let me know what day and time this week works best for your schedule, or feel free to suggest a few preferred windows?

Looking forward to connecting!

Best regards,
Kanna Narendra Kumar
Senior SEO Specialist & AI Search Strategist
Mobile / WhatsApp: +91 8464094525
Portfolio: https://narendradigital.in`;
    }
  },
  audit_intake: {
    label: "Technical Audit Intake & Next Steps",
    getSubject: (lead) => `Re: Technical Audit Next Steps - ${lead.company || "Kanna Narendra"}`,
    getMessage: (lead) => {
      const firstName = lead.name?.split(" ")[0] || "there";
      const company = lead.company || "your platform";
      return `Hi ${firstName},

Thanks for submitting your SEO audit request for ${company}.

I am beginning a preliminary diagnostic of your technical search health and AI search visibility. To ensure I deliver the most actionable insights for your team, could you please share:

1. What are your top 2-3 target market regions or primary buyer personas?
2. Who are your top 2 competitors in organic search right now?
3. Have you completed any recent migrations or CMS transitions?

Once I have these details, I will compile your custom findings and walk you through the key levers.

Best regards,
Kanna Narendra Kumar
Senior SEO Specialist & AI Search Strategist
+91 8464094525 | https://narendradigital.in`;
    }
  },
  role_discussion: {
    label: "Senior Role / Consulting Follow-up",
    getSubject: (lead) => `Re: Discussion regarding SEO Leadership & Strategic Collaboration`,
    getMessage: (lead) => {
      const firstName = lead.name?.split(" ")[0] || "there";
      const company = lead.company || "your organization";
      return `Hi ${firstName},

Thank you for contacting me regarding opportunities with ${company}.

I am currently open to high-impact Senior SEO & AI Search Strategy roles and consulting engagements where I can spearhead organic scaling, AI answer engine visibility (AEO), and enterprise search pipeline.

I would be glad to coordinate a call to learn more about your team's objectives and discuss how my 8+ years of B2B SaaS experience can support your goals.

Please let me know a convenient time for a brief introductory call.

Best regards,
Kanna Narendra Kumar
Senior SEO Specialist & AI Search Strategist
+91 8464094525 | https://narendradigital.in`;
    }
  },
  blank: {
    label: "Custom Blank Message",
    getSubject: (lead) => `Re: Inquiry - Kanna Narendra Kumar`,
    getMessage: (lead) => `Hi ${lead.name?.split(" ")[0] || "there"},



Best regards,
Kanna Narendra Kumar
Senior SEO Specialist & AI Search Strategist
+91 8464094525
https://narendradigital.in`
  }
};

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem("admin_auth") === "true" && !!sessionStorage.getItem("admin_token");
  });
  const [passcode, setPasscode] = useState("");
  const [passcodeError, setPasscodeError] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedLead, setSelectedLead] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // In-Panel Reply Modal State
  const [replyModal, setReplyModal] = useState({
    isOpen: false,
    lead: null,
    subject: "",
    message: "",
    selectedTemplate: "discovery",
    isSending: false,
    successMessage: null,
    errorMessage: null
  });

  // Test Notification State
  const [testNotificationState, setTestNotificationState] = useState({
    isSending: false,
    alertResult: null,
    showModal: false
  });

  // Email Config / Settings Modal
  const [emailSettingsModal, setEmailSettingsModal] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState({
    enabled: false,
    host: "smtp.gmail.com",
    port: 465,
    secure: "ssl",
    user: "narendrakumarkanna@gmail.com",
    pass: "",
    hasPassword: false,
    isSaving: false,
    saveSuccess: false
  });

  // Helper to attach authorization header
  const getAuthHeader = () => {
    const token = sessionStorage.getItem("admin_token") || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Secure fetch wrapper that auto-logs out on 401
  const authFetch = async (url, options = {}) => {
    const headers = {
      ...getAuthHeader(),
      ...(options.headers || {})
    };
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      handleLogout();
      throw new Error("Session expired or unauthorized");
    }
    return res;
  };

  // Authenticate on server
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setIsLoggingIn(true);
    setPasscodeError(false);

    try {
      const res = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode: passcode.trim() })
      });

      if (!res.ok) {
        setPasscodeError(true);
        setIsLoggingIn(false);
        return;
      }

      const data = await res.json();
      if (data.token) {
        sessionStorage.setItem("admin_token", data.token);
        sessionStorage.setItem("admin_auth", "true");
        setIsAuthenticated(true);
        setPasscodeError(false);
      } else {
        setPasscodeError(true);
      }
    } catch (err) {
      console.error("Login error:", err);
      setPasscodeError(true);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    sessionStorage.removeItem("admin_token");
    setIsAuthenticated(false);
    setPasscode("");
  };

  // Fetch Requests with Bearer Auth
  const fetchRequests = async () => {
    try {
      setRefreshing(true);
      const res = await authFetch("/api/audit-requests");
      if (!res.ok) {
        throw new Error("Failed to load requests from server");
      }
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.warn("API load error, checking local safety fallback:", err);
      const local = localStorage.getItem("narendra_audit_requests_backup");
      if (local) {
        try {
          setRequests(JSON.parse(local));
        } catch {
          setRequests([]);
        }
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch SMTP Config with Bearer Auth
  const fetchMailConfig = async () => {
    try {
      const res = await authFetch("/api/mail-config");
      if (res.ok) {
        const data = await res.json();
        setSmtpConfig((prev) => ({
          ...prev,
          enabled: !!data.enabled,
          host: data.host || "smtp.gmail.com",
          port: data.port || 465,
          secure: data.secure || "ssl",
          user: data.user || "narendrakumarkanna@gmail.com",
          hasPassword: !!data.hasPassword
        }));
      }
    } catch (err) {
      console.warn("Could not load mail config:", err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRequests();
      fetchMailConfig();
    }
  }, [isAuthenticated]);

  // Update Status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const res = await authFetch(`/api/audit-requests/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setRequests((prev) =>
          prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
        );
        if (selectedLead?.id === id) {
          setSelectedLead((prev) => ({ ...prev, status: newStatus }));
        }
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Delete Request
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request record?")) return;
    try {
      const res = await authFetch(`/api/audit-requests/${id}`, {
        method: "DELETE"
      });
      if (res.ok) {
        setRequests((prev) => prev.filter((item) => item.id !== id));
        if (selectedLead?.id === id) setSelectedLead(null);
        if (replyModal.lead?.id === id) setReplyModal({ ...replyModal, isOpen: false });
      }
    } catch (err) {
      console.error("Error deleting request:", err);
    }
  };

  // Open Reply Modal
  const openReplyModal = (lead) => {
    const template = RESPONSE_TEMPLATES.discovery;
    setReplyModal({
      isOpen: true,
      lead: lead,
      subject: template.getSubject(lead),
      message: template.getMessage(lead),
      selectedTemplate: "discovery",
      isSending: false,
      successMessage: null,
      errorMessage: null
    });
  };

  // Switch Template
  const handleSelectTemplate = (templateKey) => {
    const template = RESPONSE_TEMPLATES[templateKey];
    if (!template || !replyModal.lead) return;
    setReplyModal((prev) => ({
      ...prev,
      selectedTemplate: templateKey,
      subject: template.getSubject(prev.lead),
      message: template.getMessage(prev.lead),
      errorMessage: null
    }));
  };

  // Send Reply from Panel
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyModal.lead || !replyModal.message.trim()) return;

    setReplyModal((prev) => ({ ...prev, isSending: true, errorMessage: null, successMessage: null }));

    try {
      const payload = {
        leadId: replyModal.lead.id,
        toEmail: replyModal.lead.email,
        toName: replyModal.lead.name,
        subject: replyModal.subject,
        message: replyModal.message
      };

      const res = await authFetch("/api/reply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to dispatch email reply");
      }

      // Update lead in state to "Contacted" and append reply
      const updatedReplies = [
        ...(replyModal.lead.replies || []),
        { sentAt: new Date().toISOString(), subject: replyModal.subject, message: replyModal.message }
      ];

      setRequests((prev) =>
        prev.map((item) =>
          item.id === replyModal.lead.id
            ? { ...item, status: "Contacted", replies: updatedReplies }
            : item
        )
      );

      if (selectedLead?.id === replyModal.lead.id) {
        setSelectedLead((prev) => ({ ...prev, status: "Contacted", replies: updatedReplies }));
      }

      setReplyModal((prev) => ({
        ...prev,
        isSending: false,
        successMessage: `✅ Reply successfully dispatched to ${replyModal.lead.email}! Status updated to Contacted.`
      }));

      setTimeout(() => {
        setReplyModal((prev) => ({ ...prev, isOpen: false, successMessage: null }));
      }, 2500);

    } catch (err) {
      console.error("Reply error:", err);
      setReplyModal((prev) => ({
        ...prev,
        isSending: false,
        errorMessage: `Failed to send email: ${err.message}. You can still use the 1-click 'Open in Gmail' button below.`
      }));
    }
  };

  // Trigger Live Test Notification Email
  const handleTriggerTestEmail = async () => {
    setTestNotificationState({ isSending: true, alertResult: null, showModal: true });
    try {
      const res = await authFetch("/api/test-notification", { method: "POST" });
      const data = await res.json();
      setTestNotificationState({
        isSending: false,
        alertResult: data,
        showModal: true
      });
    } catch (err) {
      setTestNotificationState({
        isSending: false,
        alertResult: { success: false, error: err.message },
        showModal: true
      });
    }
  };

  // Save SMTP Settings
  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSmtpConfig((prev) => ({ ...prev, isSaving: true, saveSuccess: false }));

    try {
      const res = await authFetch("/api/mail-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enabled: smtpConfig.enabled,
          host: smtpConfig.host,
          port: smtpConfig.port,
          secure: smtpConfig.secure,
          user: smtpConfig.user,
          pass: smtpConfig.pass
        })
      });

      if (res.ok) {
        setSmtpConfig((prev) => ({
          ...prev,
          isSaving: false,
          saveSuccess: true,
          hasPassword: !!prev.pass || prev.hasPassword
        }));
        setTimeout(() => {
          setSmtpConfig((prev) => ({ ...prev, saveSuccess: false }));
        }, 3000);
      }
    } catch (err) {
      console.error("Failed to save SMTP config:", err);
      setSmtpConfig((prev) => ({ ...prev, isSaving: false }));
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!requests.length) return;
    const headers = ["ID", "Date", "Name", "Email", "Company/Domain", "Primary Need", "Status", "Message"];
    const rows = requests.map((r) => [
      `"${r.id || ""}"`,
      `"${new Date(r.createdAt).toLocaleString()}"`,
      `"${(r.name || "").replace(/"/g, '""')}"`,
      `"${(r.email || "").replace(/"/g, '""')}"`,
      `"${(r.company || "").replace(/"/g, '""')}"`,
      `"${(r.needs || "").replace(/"/g, '""')}"`,
      `"${r.status || "New"}"`,
      `"${(r.message || "").replace(/"/g, '""').replace(/\n/g, " ")}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `narendra-seo-audit-requests-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filtered list
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const matchesStatus = statusFilter === "All" || (r.status || "New") === statusFilter;
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !query ||
        r.name?.toLowerCase().includes(query) ||
        r.email?.toLowerCase().includes(query) ||
        r.company?.toLowerCase().includes(query) ||
        r.needs?.toLowerCase().includes(query) ||
        r.message?.toLowerCase().includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [requests, statusFilter, searchQuery]);

  // Stats calculation
  const stats = useMemo(() => {
    return {
      total: requests.length,
      newCount: requests.filter((r) => !r.status || r.status === "New").length,
      inReview: requests.filter((r) => r.status === "In Review").length,
      contacted: requests.filter((r) => r.status === "Contacted").length,
      completed: requests.filter((r) => r.status === "Completed").length
    };
  }, [requests]);

  // Format date nicely
  const formatDate = (isoStr) => {
    if (!isoStr) return "Just now";
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return isoStr;
    }
  };

  // Passcode Lock View
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#070a12] text-white flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="max-w-md w-full bg-[#0c1222] border border-white/10 rounded-3xl p-8 shadow-2xl relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7" />
          </div>

          <h2 className="text-2xl font-bold font-heading text-center mb-2">
            Narendra's Lead Console
          </h2>
          <p className="text-xs text-white/50 text-center mb-8">
            Enter your admin PIN to review audit inquiries and respond to client leads.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-white/60 mb-2">
                Admin Passcode
              </label>
              <input
                type="password"
                autoFocus
                value={passcode}
                onChange={(e) => {
                  setPasscode(e.target.value);
                  setPasscodeError(false);
                }}
                placeholder="Enter PIN (e.g. narendra2026)"
                className={`w-full px-4 py-3 rounded-xl bg-[#080d19] border ${
                  passcodeError ? "border-rose-500" : "border-white/15"
                } text-white placeholder-white/30 text-sm focus:outline-none focus:border-cyan-400 transition-colors`}
              />
              {passcodeError && (
                <p className="text-xs text-rose-400 mt-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> Invalid passcode. Hint: use <code>narendra2026</code>
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full py-3.5 rounded-xl btn-bold-primary text-white font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
              <span>{isLoggingIn ? "Verifying..." : "Unlock Admin Panel"}</span>
            </button>

            <div className="pt-4 text-center">
              <Link
                to="/"
                className="text-xs text-white/40 hover:text-white/80 inline-flex items-center gap-1.5 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Portfolio
              </Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070a12] text-white">
      {/* Top Header */}
      <header className="border-b border-white/10 bg-[#0c1222]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/"
              className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
              title="Back to Website"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-extrabold text-base sm:text-lg text-white">
                  Audit & Leads Console
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  Admin
                </span>
              </div>
              <p className="text-[11px] text-white/50 hidden md:block">
                Inbound alerts route to <span className="text-cyan-400 font-mono">narendrakumarkanna@gmail.com</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-2.5">
            {/* Send Test Email Button */}
            <button
              onClick={handleTriggerTestEmail}
              disabled={testNotificationState.isSending}
              className="px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-xs font-mono text-cyan-300 flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Send a test notification to your Gmail right now"
            >
              {testNotificationState.isSending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
              ) : (
                <Bell className="w-3.5 h-3.5 text-cyan-400" />
              )}
              <span className="hidden sm:inline">Test Alert</span>
            </button>

            {/* Email / SMTP Settings Button */}
            <button
              onClick={() => setEmailSettingsModal(true)}
              className="p-2 sm:px-3 sm:py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 text-xs font-mono text-white/80 hover:text-white flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Email delivery & SMTP settings"
            >
              <Settings className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Mail Settings</span>
            </button>

            <button
              onClick={fetchRequests}
              disabled={refreshing}
              className="p-2 sm:px-3 sm:py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 text-xs font-mono text-white/80 hover:text-white flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Refresh leads list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-cyan-400" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <button
              onClick={handleExportCSV}
              className="p-2 sm:px-3 sm:py-2 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-xs font-mono text-emerald-300 flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Export all requests to CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">CSV</span>
            </button>

            <button
              onClick={handleLogout}
              className="p-2 sm:px-3 sm:py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-xs font-mono text-rose-300 flex items-center gap-1 transition-colors cursor-pointer"
              title="Lock Admin Panel"
            >
              <Lock className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Lock</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Informational Banner on Email Delivery */}
        <div className="p-4 rounded-2xl bg-[#0d152a] border border-cyan-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-lg shadow-cyan-950/20">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shrink-0">
              <Inbox className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-semibold text-white">
                Inbound Form Alerts are active for <span className="text-cyan-300 font-mono">narendrakumarkanna@gmail.com</span>
              </p>
              <p className="text-[11px] text-white/60">
                If emails don't appear in your Primary inbox, please check your <strong className="text-white">Spam / Junk</strong> folder and mark as <span className="text-emerald-400">"Report Not Spam"</span>.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
            <button
              onClick={handleTriggerTestEmail}
              disabled={testNotificationState.isSending}
              className="px-3 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 text-xs font-mono font-semibold border border-cyan-500/30 transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <Bell className="w-3 h-3" />
              <span>Send Test Email</span>
            </button>
          </div>
        </div>

        {/* KPI Metrics Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-5 rounded-2xl bg-[#0c1222] border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase text-white/50">Total Inquiries</span>
              <Building2 className="w-4 h-4 text-white/40" />
            </div>
            <div className="text-3xl font-extrabold font-heading text-white">{stats.total}</div>
            <div className="text-[11px] text-white/40 mt-1">All recorded inquiries</div>
          </div>

          <div className="p-5 rounded-2xl bg-[#0c1222] border border-emerald-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase text-emerald-400 font-semibold">New / Unread</span>
              <Sparkles className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-3xl font-extrabold font-heading text-emerald-400">{stats.newCount}</div>
            <div className="text-[11px] text-white/40 mt-1">Requires follow-up</div>
          </div>

          <div className="p-5 rounded-2xl bg-[#0c1222] border border-cyan-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase text-cyan-400 font-semibold">Contacted</span>
              <Send className="w-4 h-4 text-cyan-400" />
            </div>
            <div className="text-3xl font-extrabold font-heading text-cyan-400">{stats.contacted}</div>
            <div className="text-[11px] text-white/40 mt-1">Replies dispatched</div>
          </div>

          <div className="p-5 rounded-2xl bg-[#0c1222] border border-purple-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase text-purple-400 font-semibold">Completed</span>
              <CheckCircle2 className="w-4 h-4 text-purple-400" />
            </div>
            <div className="text-3xl font-extrabold font-heading text-purple-400">{stats.completed}</div>
            <div className="text-[11px] text-white/40 mt-1">Audits delivered</div>
          </div>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="p-4 rounded-2xl bg-[#0c1222] border border-white/10 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by client name, email, website domain, or message keywords..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white placeholder-white/30 text-xs focus:outline-none focus:border-cyan-400 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white text-xs"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            {["All", "New", "In Review", "Contacted", "Completed"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-xs font-mono whitespace-nowrap transition-all cursor-pointer ${
                  statusFilter === status
                    ? "bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-600/30"
                    : "bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08]"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Requests Table / Cards */}
        {loading ? (
          <div className="py-24 text-center space-y-3">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm font-mono text-white/50">Loading audit requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-12 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-white/[0.04] text-white/40 flex items-center justify-center mx-auto">
              <MessageSquare className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-bold text-lg text-white">No requests found</h3>
            <p className="text-xs text-white/50 max-w-sm mx-auto">
              {searchQuery || statusFilter !== "All"
                ? "Try adjusting your search or filters to find specific inquiries."
                : "No audit inquiries submitted yet. When visitors fill the contact form, they will appear right here."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map((req) => {
              const status = STATUS_CONFIG[req.status] || STATUS_CONFIG.New;
              const hasReplies = req.replies && req.replies.length > 0;
              return (
                <div
                  key={req.id}
                  className="p-5 sm:p-6 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-white/20 transition-all shadow-xl group flex flex-col lg:flex-row lg:items-center justify-between gap-5"
                >
                  {/* Left: Lead Info */}
                  <div className="space-y-2.5 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold border ${status.bg} ${status.text} ${status.border}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                        {req.status || "New"}
                      </span>

                      <span className="text-xs text-white/40 font-mono flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-white/30" />
                        {formatDate(req.createdAt)}
                      </span>

                      <span className="px-2.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-300 text-[11px] font-mono border border-indigo-500/20">
                        {req.needs || "SEO Strategy"}
                      </span>

                      {hasReplies && (
                        <span className="px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-300 text-[10px] font-mono border border-cyan-500/30 flex items-center gap-1">
                          <Check className="w-3 h-3 text-cyan-400" />
                          <span>Replied ({req.replies.length})</span>
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <h4 className="text-base font-bold font-heading text-white flex items-center gap-2">
                        <User className="w-4 h-4 text-cyan-400" />
                        {req.name || "Anonymous Prospect"}
                      </h4>

                      <div className="flex items-center gap-1.5 text-xs text-white/70">
                        <Mail className="w-3.5 h-3.5 text-white/40" />
                        <a
                          href={`mailto:${req.email}`}
                          className="hover:text-cyan-400 transition-colors underline decoration-white/20 hover:decoration-cyan-400"
                        >
                          {req.email}
                        </a>
                      </div>

                      {req.company && (
                        <div className="flex items-center gap-1.5 text-xs text-white/70">
                          <Globe className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-emerald-300 font-mono">{req.company}</span>
                        </div>
                      )}
                    </div>

                    {/* Message Preview */}
                    <p className="text-xs text-white/70 line-clamp-2 leading-relaxed bg-[#080d19] p-3 rounded-xl border border-white/[0.06] font-normal">
                      "{req.message || "No project notes provided."}"
                    </p>
                  </div>

                  {/* Right: Actions & In-Panel Reply Trigger */}
                  <div className="flex flex-wrap items-center gap-2.5 lg:flex-shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-white/[0.08]">
                    {/* Status Dropdown */}
                    <select
                      value={req.status || "New"}
                      onChange={(e) => handleUpdateStatus(req.id, e.target.value)}
                      className="px-3 py-2 rounded-xl bg-[#080d19] border border-white/15 text-xs font-mono text-white/90 focus:outline-none focus:border-cyan-400 cursor-pointer"
                    >
                      <option value="New">Status: New</option>
                      <option value="In Review">Status: In Review</option>
                      <option value="Contacted">Status: Contacted</option>
                      <option value="Completed">Status: Completed</option>
                    </select>

                    {/* In-Panel Reply Button (Opens Composer Modal) */}
                    <button
                      onClick={() => openReplyModal(req)}
                      className="px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-500/20 to-cyan-500/20 hover:from-indigo-500/30 hover:to-cyan-500/30 border border-cyan-500/30 text-xs font-semibold text-cyan-300 flex items-center gap-1.5 transition-all shadow-md shadow-cyan-500/10 cursor-pointer"
                      title="Reply to lead directly from the panel"
                    >
                      <Send className="w-3.5 h-3.5 text-cyan-400" />
                      <span>Reply from Panel</span>
                    </button>

                    {/* WhatsApp Quick Action if applicable */}
                    <a
                      href={`https://wa.me/918464094525?text=${encodeURIComponent(
                        `Hi ${req.name}, regarding your SEO audit inquiry for ${req.company || "your website"}`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 text-emerald-300 transition-colors"
                      title="Open WhatsApp chat link"
                    >
                      <PhoneCall className="w-3.5 h-3.5" />
                    </a>

                    {/* View Details Modal Trigger */}
                    <button
                      onClick={() => setSelectedLead(req)}
                      className="p-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors cursor-pointer"
                      title="View full lead details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    {/* Delete Lead */}
                    <button
                      onClick={() => handleDelete(req.id)}
                      className="p-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 transition-colors cursor-pointer"
                      title="Delete entry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ---------------------------------------------------- */}
      {/* IN-PANEL REPLY COMPOSER MODAL                        */}
      {/* ---------------------------------------------------- */}
      {replyModal.isOpen && replyModal.lead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-[#0c1222] border border-cyan-500/20 rounded-3xl max-w-2xl w-full p-6 sm:p-7 shadow-2xl shadow-cyan-950/40 relative space-y-5 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-white/10 pb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                    <Send className="w-4 h-4" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white">
                    Reply to {replyModal.lead.name}
                  </h3>
                </div>
                <p className="text-xs text-white/50 font-mono">
                  Recipient: <span className="text-cyan-300">{replyModal.lead.email}</span> &bull; {replyModal.lead.company || "No company specified"}
                </p>
              </div>

              <button
                onClick={() => setReplyModal({ ...replyModal, isOpen: false })}
                className="p-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Client Inquiry Context Box */}
            <div className="p-3.5 rounded-xl bg-[#080d19] border border-white/[0.08] text-xs space-y-1">
              <div className="flex justify-between text-white/50 font-mono">
                <span>Requested Service:</span>
                <span className="text-indigo-300 font-semibold">{replyModal.lead.needs}</span>
              </div>
              <div className="text-white/70 italic line-clamp-2">
                "{replyModal.lead.message || "No custom message provided."}"
              </div>
            </div>

            {/* Quick Template Picker */}
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-white/60 mb-2">
                Quick Response Template
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(RESPONSE_TEMPLATES).map(([key, tpl]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleSelectTemplate(key)}
                    className={`px-3 py-2 rounded-xl text-xs text-left font-medium transition-all cursor-pointer border ${
                      replyModal.selectedTemplate === key
                        ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300 font-semibold shadow-sm shadow-cyan-500/20"
                        : "bg-[#080d19] border-white/10 text-white/70 hover:text-white hover:border-white/20"
                    }`}
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <form onSubmit={handleSendReply} className="space-y-4">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-white/60 mb-1.5">
                  Email Subject Line
                </label>
                <input
                  type="text"
                  required
                  value={replyModal.subject}
                  onChange={(e) => setReplyModal({ ...replyModal, subject: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-[#080d19] border border-white/15 text-white placeholder-white/30 text-xs sm:text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider text-white/60 mb-1.5">
                  Message Body (Sent directly to client)
                </label>
                <textarea
                  required
                  rows={9}
                  value={replyModal.message}
                  onChange={(e) => setReplyModal({ ...replyModal, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-[#080d19] border border-white/15 text-white placeholder-white/30 text-xs sm:text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none leading-relaxed font-sans"
                />
              </div>

              {/* Status messages */}
              {replyModal.successMessage && (
                <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2 animate-in fade-in">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                  <span>{replyModal.successMessage}</span>
                </div>
              )}

              {replyModal.errorMessage && (
                <div className="p-3 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2 animate-in fade-in">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                  <span>{replyModal.errorMessage}</span>
                </div>
              )}

              {/* Actions Strip */}
              <div className="pt-2 border-t border-white/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                {/* 1-Click External Alternative Buttons */}
                <div className="flex items-center gap-2">
                  <a
                    href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                      replyModal.lead.email
                    )}&su=${encodeURIComponent(replyModal.subject)}&body=${encodeURIComponent(
                      replyModal.message
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white/80 hover:text-white text-xs font-mono flex items-center gap-1.5 transition-colors"
                    title="Open this pre-filled email in Gmail Web in a new tab"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-red-400" />
                    <span>Open in Gmail</span>
                  </a>

                  <a
                    href={`mailto:${replyModal.lead.email}?subject=${encodeURIComponent(
                      replyModal.subject
                    )}&body=${encodeURIComponent(replyModal.message)}`}
                    className="px-3 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white/80 hover:text-white text-xs font-mono flex items-center gap-1.5 transition-colors"
                    title="Open in your default desktop mail app (Outlook, Apple Mail, etc.)"
                  >
                    <Mail className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Mail App</span>
                  </a>
                </div>

                {/* Primary Send Button */}
                <button
                  type="submit"
                  disabled={replyModal.isSending}
                  className="px-6 py-2.5 rounded-xl btn-bold-primary text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-cyan-500/20 disabled:opacity-50"
                >
                  {replyModal.isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sending to Client...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send from Panel</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* TEST NOTIFICATION MODAL                              */}
      {/* ---------------------------------------------------- */}
      {testNotificationState.showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[#0c1222] border border-white/15 rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl relative space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                  <Bell className="w-4 h-4" />
                </div>
                <h3 className="font-heading font-bold text-base text-white">
                  Email Alert Dispatch Test
                </h3>
              </div>
              <button
                onClick={() => setTestNotificationState({ ...testNotificationState, showModal: false })}
                className="p-1.5 rounded-lg text-white/60 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {testNotificationState.isSending ? (
              <div className="py-8 text-center space-y-3">
                <Loader2 className="w-7 h-7 animate-spin text-cyan-400 mx-auto" />
                <p className="text-xs text-white/60 font-mono">Dispatched test alert through server mail engine...</p>
              </div>
            ) : testNotificationState.alertResult?.success ? (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 space-y-2">
                  <div className="flex items-center gap-2 font-bold text-sm text-emerald-400">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Notification Dispatched Successfully!</span>
                  </div>
                  <p className="text-xs text-white/80 leading-relaxed">
                    A test lead alert was sent to <strong className="text-white">narendrakumarkanna@gmail.com</strong>.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-[#080d19] border border-white/[0.08] text-xs text-white/70 space-y-2">
                  <div className="font-semibold text-white flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5 text-cyan-400" />
                    <span>Important Gmail Inbox Note:</span>
                  </div>
                  <ul className="list-disc pl-4 space-y-1 text-white/60">
                    <li>Open your Gmail and look for subject: <strong className="text-white">"🔥 New SEO Lead: Console Test Lead"</strong>.</li>
                    <li>If it landed in <strong className="text-amber-300">Spam / Junk</strong>, click into it and click <strong className="text-emerald-300">"Report Not Spam"</strong>. Once marked, Gmail will deliver future leads straight to your Primary inbox.</li>
                  </ul>
                </div>

                <button
                  onClick={() => setTestNotificationState({ ...testNotificationState, showModal: false })}
                  className="w-full py-2.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.15] text-white text-xs font-mono uppercase tracking-wider transition-colors"
                >
                  Close & Check Inbox
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs">
                  <p className="font-bold mb-1">Dispatch Error</p>
                  <p>{testNotificationState.alertResult?.error || "Could not complete mail transmission."}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* EMAIL & SMTP SETTINGS MODAL                          */}
      {/* ---------------------------------------------------- */}
      {emailSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-[#0c1222] border border-white/15 rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl relative space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                  <Settings className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base text-white">Email Delivery Settings</h3>
                  <p className="text-[11px] text-white/50">Configure notification routing and optional Gmail SMTP</p>
                </div>
              </div>
              <button
                onClick={() => setEmailSettingsModal(false)}
                className="p-1.5 rounded-lg text-white/60 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Notification Recipient Display */}
            <div className="p-4 rounded-2xl bg-[#080d19] border border-white/[0.08] space-y-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 block">
                Current Notification Recipient
              </span>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-cyan-300 font-mono">
                  narendrakumarkanna@gmail.com
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  Active
                </span>
              </div>
              <p className="text-[11px] text-white/50">
                Every visitor inquiry from your contact form immediately triggers an email alert to this address.
              </p>
            </div>

            {/* Optional SMTP Config Form */}
            <form onSubmit={handleSaveSmtp} className="space-y-4">
              <div className="flex items-center justify-between pt-2 border-t border-white/[0.08]">
                <div>
                  <span className="text-xs font-semibold text-white block">Custom SMTP (Optional)</span>
                  <span className="text-[11px] text-white/50 block">Send via authenticated Gmail for 100% Primary inbox guarantee</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smtpConfig.enabled}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                </label>
              </div>

              {smtpConfig.enabled && (
                <div className="space-y-3 pt-2 animate-in fade-in">
                  <div>
                    <label className="block text-[11px] font-mono text-white/60 mb-1">SMTP Host</label>
                    <input
                      type="text"
                      value={smtpConfig.host}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-[#080d19] border border-white/15 text-xs text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-mono text-white/60 mb-1">Port</label>
                      <input
                        type="number"
                        value={smtpConfig.port}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, port: Number(e.target.value) })}
                        className="w-full px-3 py-2 rounded-xl bg-[#080d19] border border-white/15 text-xs text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-mono text-white/60 mb-1">Security</label>
                      <select
                        value={smtpConfig.secure}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, secure: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-[#080d19] border border-white/15 text-xs text-white"
                      >
                        <option value="ssl">SSL (Port 465)</option>
                        <option value="tls">TLS (Port 587)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono text-white/60 mb-1">Gmail / User Address</label>
                    <input
                      type="email"
                      value={smtpConfig.user}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-[#080d19] border border-white/15 text-xs text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-mono text-white/60 mb-1">
                      Google App Password (16 chars)
                      {smtpConfig.hasPassword && <span className="text-emerald-400 ml-2">(Saved on server)</span>}
                    </label>
                    <input
                      type="password"
                      placeholder={smtpConfig.hasPassword ? "•••••••••••••••• (Leave blank to keep)" : "xxxx xxxx xxxx xxxx"}
                      value={smtpConfig.pass}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, pass: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-[#080d19] border border-white/15 text-xs text-white"
                    />
                    <p className="text-[10px] text-white/40 mt-1">
                      To generate: Google Account &rarr; Security &rarr; 2-Step Verification &rarr; App Passwords.
                    </p>
                  </div>
                </div>
              )}

              {smtpConfig.saveSuccess && (
                <div className="p-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Mail settings saved successfully!</span>
                </div>
              )}

              <button
                type="submit"
                disabled={smtpConfig.isSaving}
                className="w-full py-2.5 rounded-xl btn-bold-primary text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2"
              >
                {smtpConfig.isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                <span>Save Email Settings</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* LEAD DETAIL MODAL                                    */}
      {/* ---------------------------------------------------- */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0c1222] border border-white/15 rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl relative space-y-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-white/10 pb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-heading font-bold text-xl text-white">
                    {selectedLead.name}
                  </span>
                  <span
                    className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                      STATUS_CONFIG[selectedLead.status]?.bg || "bg-emerald-500/15"
                    } ${STATUS_CONFIG[selectedLead.status]?.text || "text-emerald-400"} ${
                      STATUS_CONFIG[selectedLead.status]?.border || "border-emerald-500/30"
                    }`}
                  >
                    {selectedLead.status || "New"}
                  </span>
                </div>
                <p className="text-xs text-white/50 font-mono">
                  Submitted on {formatDate(selectedLead.createdAt)} (ID: {selectedLead.id})
                </p>
              </div>

              <button
                onClick={() => setSelectedLead(null)}
                className="p-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Grid of details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-[#080d19] border border-white/[0.06]">
                <span className="text-[10px] font-mono uppercase text-white/40 block mb-1">
                  Email Address
                </span>
                <div className="flex items-center justify-between">
                  <a
                    href={`mailto:${selectedLead.email}`}
                    className="text-sm font-semibold text-cyan-400 hover:underline truncate"
                  >
                    {selectedLead.email}
                  </a>
                  <button
                    onClick={() => copyToClipboard(selectedLead.email, "email")}
                    className="text-white/40 hover:text-white ml-2"
                    title="Copy Email"
                  >
                    {copiedId === "email" ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-[#080d19] border border-white/[0.06]">
                <span className="text-[10px] font-mono uppercase text-white/40 block mb-1">
                  Website / Company
                </span>
                <span className="text-sm font-semibold text-emerald-300 font-mono">
                  {selectedLead.company || "Not provided"}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-[#080d19] border border-white/[0.06] sm:col-span-2">
                <span className="text-[10px] font-mono uppercase text-white/40 block mb-1">
                  Selected Service / Audit Type
                </span>
                <span className="text-sm font-bold text-indigo-300">
                  {selectedLead.needs || "General Inbound Lead"}
                </span>
              </div>
            </div>

            {/* Full Message */}
            <div className="p-4 rounded-xl bg-[#080d19] border border-white/[0.06] space-y-2">
              <span className="text-[10px] font-mono uppercase text-white/40 block">
                Prospect's Project Scope / Message
              </span>
              <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
                {selectedLead.message}
              </p>
            </div>

            {/* Sent Replies History if any */}
            {selectedLead.replies && selectedLead.replies.length > 0 && (
              <div className="p-4 rounded-xl bg-[#080d19] border border-cyan-500/20 space-y-2">
                <span className="text-[10px] font-mono uppercase text-cyan-400 font-semibold block">
                  Replies History ({selectedLead.replies.length})
                </span>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {selectedLead.replies.map((rep, idx) => (
                    <div key={idx} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-xs">
                      <div className="flex justify-between text-white/50 font-mono text-[10px] mb-1">
                        <span>{formatDate(rep.sentAt)}</span>
                        <span className="text-cyan-300 font-semibold">{rep.subject}</span>
                      </div>
                      <p className="text-white/80 line-clamp-2">{rep.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-white/60">Update Status:</span>
                <select
                  value={selectedLead.status || "New"}
                  onChange={(e) => handleUpdateStatus(selectedLead.id, e.target.value)}
                  className="px-3 py-1.5 rounded-lg bg-[#080d19] border border-white/15 text-xs font-mono text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="New">New</option>
                  <option value="In Review">In Review</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDelete(selectedLead.id)}
                  className="px-3 py-2 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 text-xs font-mono flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>

                <button
                  onClick={() => {
                    const lead = selectedLead;
                    setSelectedLead(null);
                    openReplyModal(lead);
                  }}
                  className="px-4 py-2 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Reply from Panel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
